import React from "react";

import { css, cx } from "@emotion/css";
import { zodResolver } from "@hookform/resolvers/zod";
import { Resolver, useForm, UseFormReturn } from "react-hook-form";

import { formatTextToRichText } from "@/hooks/useRichTextState";
import { useStyles } from "@/hooks/useStyles.ts";
import { Form } from "@/uiKit/atoms/forms/Form.tsx";
import {
  CarePlanRenewalInfo,
  carePlanRenewalSchema,
  SimpleCarePlanRenewalInfo,
} from "@/uiKit/organisms/PatientCreationAndRenewal/CarePlan/CarePlanRenewal.ts";
import { useRenewalContext } from "@/uiKit/organisms/PatientCreationAndRenewal/PatientRenewalProvider.tsx";

export const CARE_PLAN_RENEWAL_FORM_ID = "care-plan-renewal-info-form";

export type CarePlanRenewalFormProps = {
  carePlanInfo?: Partial<SimpleCarePlanRenewalInfo>;
  onSubmit: (data: CarePlanRenewalInfo) => void;
  className?: string;
  children?: React.ReactNode | ((formContext: UseFormReturn<SimpleCarePlanRenewalInfo>) => React.ReactNode);
};

export const CarePlanRenewalForm: React.FC<CarePlanRenewalFormProps> = ({
  carePlanInfo,
  onSubmit,
  children,
  className,
}) => {
  const styles = useStyles(makeStyles);
  const { carePlanDirectives } = useRenewalContext();
  const defaultNurse =
    carePlanDirectives.telemonitoringNurses.length >= 1 ? carePlanDirectives.telemonitoringNurses[0].id : undefined;
  const form = useForm<SimpleCarePlanRenewalInfo>({
    resolver: zodResolver(carePlanRenewalSchema) as Resolver<SimpleCarePlanRenewalInfo>,
    defaultValues: {
      duration: "3",
      ...carePlanInfo,
      description: formatTextToRichText(carePlanInfo?.description),
      telemonitoringNurseId: defaultNurse,
      termDate: carePlanInfo?.diabetesType === "gestational" ? carePlanInfo.termDate : undefined,
    },
  });

  return (
    <Form
      id={CARE_PLAN_RENEWAL_FORM_ID}
      formContext={form}
      onSubmit={data =>
        onSubmit({
          ...data,
          periodStart: carePlanDirectives.carePlanCreationDataDirectives.periodStart,
          inclusionTier: carePlanDirectives.telemonitoringTierMap[data.inclusionCriteria],
          termDate: data.diabetesType === "gestational" ? data.termDate : undefined,
        } as CarePlanRenewalInfo)
      }
      className={cx(styles.container, className)}
    >
      {typeof children === "function" ? children(form) : children}
    </Form>
  );
};

const makeStyles = () => ({
  container: css({}),
});
